import React, { useEffect, useState } from "react";
import { Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg } from "@/lib/api";
import { AderenciaBadge } from "@/components/Badges";
import OpportunityDrawer from "@/components/OpportunityDrawer";
import { RecorrenciaAlertas } from "@/components/RecorrenciaAlertas";

const COL_ACCENT = {
  NOVA: "border-t-sky-400", ANALISAR: "border-t-blue-400", FAVORITA: "border-t-violet-400",
  "DOCUMENTAÇÃO": "border-t-indigo-400", "PROPOSTA EM PREPARAÇÃO": "border-t-amber-400",
  "PROPOSTA ENVIADA": "border-t-cyan-400", "EM DISPUTA": "border-t-orange-400",
  GANHA: "border-t-emerald-500", PERDIDA: "border-t-rose-500", CANCELADA: "border-t-slate-400",
};

export default function Pipeline() {
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/pipeline").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const drop = async (stage) => {
    if (!dragId) return;
    const id = dragId; setDragId(null);
    try {
      await api.patch(`/opportunities/${id}`, { stage });
      toast.success(`Movido para ${stage}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>;

  const totalSaved = Object.values(data.grouped).reduce((a, arr) => a + arr.length, 0);

  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">Pipeline de Licitações</h1>
        <p className="text-sm text-slate-500">{totalSaved} oportunidades salvas — arraste os cards entre as etapas</p>
      </div>

      <RecorrenciaAlertas onOpen={setSelId} onChanged={load} />

      {totalSaved === 0 && (
        <div className="mb-4 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhuma oportunidade no pipeline ainda. Vá ao <span className="font-semibold text-sky-600">Radar</span>, abra uma oportunidade e clique em "Salvar" ou "Para pipeline".
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-4">
        {data.stages.map((stage) => (
          <div key={stage} data-testid={`kanban-col-${stage.toLowerCase().replace(/\s|ç|ã/g, "-")}`}
            onDragOver={(e) => e.preventDefault()} onDrop={() => drop(stage)}
            className={`w-72 shrink-0 rounded-xl border border-t-4 border-slate-200 bg-slate-100/60 ${COL_ACCENT[stage] || "border-t-slate-300"}`}>
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{stage}</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500">{data.grouped[stage].length}</span>
            </div>
            <div className="space-y-2 p-2">
              {data.grouped[stage].map((o) => (
                <div key={o.id} draggable onDragStart={() => setDragId(o.id)} onClick={() => setSelId(o.id)}
                  data-testid="pipeline-card"
                  className="cursor-grab rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:shadow active:cursor-grabbing">
                  <div className="mb-1.5 flex items-start justify-between gap-1">
                    <GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />
                    <AderenciaBadge value={o.aderencia} />
                  </div>
                  {o.recorrencia?.is && <span data-testid="pipeline-card-recorrente" className="mb-1 inline-block rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">♻ Recorrente</span>}
                  <div className="text-xs font-semibold text-slate-700 line-clamp-1">{o.orgao_razao_social}</div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{o.objeto}</p>
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <span className="font-display font-bold text-slate-900">{brl(o.valor_proposto || o.valor_estimado)}</span>
                    <span className="text-slate-400">{o.uf}</span>
                  </div>
                  {stage === "PERDIDA" && o.loss_reason && <div className="mt-1 text-[10px] text-rose-600">Motivo: {o.loss_reason}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selId && <OpportunityDrawer id={selId} onClose={() => setSelId(null)} onChanged={load} />}
    </div>
  );
}
